import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { Users, Search, Trash2, KeyRound, LogIn, ShieldCheck, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ROLE_PILL = {
    admin: "bg-amber-100 text-amber-900 border border-amber-300",
    responsable: "bg-orange text-white",
    formateur: "bg-ink text-white",
    alternant: "bg-orange-50 text-orange-700 border border-orange-200",
};

export default function AdminUsers() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [roleFilter, setRoleFilter] = useState("all");

    const reload = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/users");
            setUsers(data || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        } finally { setLoading(false); }
    };

    useEffect(() => {
        if (!user) return;
        if (user.role !== "admin") { navigate("/dashboard"); return; }
        reload();
        // eslint-disable-next-line
    }, [user]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return users.filter((u) => {
            if (roleFilter !== "all" && u.role !== roleFilter) return false;
            if (!q) return true;
            return (
                (u.email || "").toLowerCase().includes(q) ||
                (u.nom || "").toLowerCase().includes(q) ||
                (u.prenom || "").toLowerCase().includes(q) ||
                (u.nom_etablissement || "").toLowerCase().includes(q)
            );
        });
    }, [users, search, roleFilter]);

    const counts = useMemo(() => {
        const c = { all: users.length, responsable: 0, formateur: 0, alternant: 0, admin: 0 };
        users.forEach((u) => { c[u.role] = (c[u.role] || 0) + 1; });
        return c;
    }, [users]);

    const removeUser = async (u) => {
        if (!window.confirm(`Supprimer définitivement ${u.email} ?\nToutes ses données (bilans, messages, cours…) seront effacées. Action IRRÉVERSIBLE.`)) return;
        try {
            await api.delete(`/admin/users/${u.id}`);
            toast.success("Compte supprimé");
            reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
    };

    const resetPwd = async (u) => {
        if (!window.confirm(`Réinitialiser le mot de passe de ${u.email} ?\nUn MDP provisoire sera généré et envoyé par e-mail.`)) return;
        try {
            const { data } = await api.post(`/admin/users/${u.id}/reset-password`, { notify_user: true });
            window.prompt(`✅ MDP provisoire pour ${u.email}\nUn e-mail a été envoyé. Tu peux aussi le copier ci-dessous :`, data.temp_password);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
    };

    const impersonate = async (u) => {
        if (u.role === "admin") return;
        if (!window.confirm(`Te connecter en tant que ${u.email} ?`)) return;
        try {
            await api.post(`/admin/impersonate/${u.id}`);
            toast.success("Connecté, redirection…");
            if (refresh) await refresh();
            navigate("/dashboard");
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
    };

    return (
        <div className="min-h-screen bg-paper">
            <Navbar />
            <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="admin-users-page">
                <Link to="/admin" className="inline-flex items-center gap-1.5 text-xs font-mono text-muted2 hover:text-ink mb-4" data-testid="admin-users-back">
                    <ArrowLeft size={12} /> Retour à la console
                </Link>
                <div className="flex items-center gap-2 mb-2">
                    <Users size={20} className="text-orange" />
                    <div className="label-eyebrow text-orange">Administration · vue globale</div>
                </div>
                <h1 className="h-display text-3xl sm:text-4xl font-semibold leading-tight mb-2">
                    Tous les <span className="text-orange">utilisateurs</span> de la plateforme
                </h1>
                <p className="text-sm text-muted2 mb-8 max-w-2xl">
                    Liste à plat de tous les comptes (responsables, formateurs, alternants) sans distinction d'établissement. Recherche, filtre par rôle, et actions rapides.
                </p>

                {/* Filters */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4" data-testid="admin-users-filters">
                    {[
                        { key: "all", label: `Tous · ${counts.all}` },
                        { key: "responsable", label: `Responsables · ${counts.responsable || 0}` },
                        { key: "formateur", label: `Formateurs · ${counts.formateur || 0}` },
                        { key: "alternant", label: `Alternants · ${counts.alternant || 0}` },
                        { key: "admin", label: `Admins · ${counts.admin || 0}` },
                    ].map((f) => (
                        <button
                            key={f.key}
                            onClick={() => setRoleFilter(f.key)}
                            className={`px-3 py-2 text-xs font-mono uppercase tracking-wider transition-colors ${roleFilter === f.key ? "bg-ink text-white" : "bg-paper border border-rule hover:bg-surface"}`}
                            data-testid={`filter-${f.key}`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>

                <div className="relative mb-6 max-w-md">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
                    <input
                        className="field-input !pl-9"
                        placeholder="Rechercher par nom, email, lycée…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="admin-users-search"
                    />
                </div>

                {loading ? (
                    <div className="panel p-10 text-center text-muted2 text-sm" data-testid="admin-users-loading">
                        <Loader2 size={16} className="animate-spin mx-auto mb-2" /> Chargement…
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="panel p-10 text-center text-sm text-muted2" data-testid="admin-users-empty">
                        Aucun utilisateur ne correspond aux filtres.
                    </div>
                ) : (
                    <div className="panel" data-testid="admin-users-list">
                        <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-rule label-eyebrow">
                            <div className="col-span-3">Utilisateur</div>
                            <div className="col-span-2">Rôle</div>
                            <div className="col-span-3">Lycée</div>
                            <div className="col-span-2">Inscrit le</div>
                            <div className="col-span-2 text-right">Actions</div>
                        </div>
                        {filtered.map((u) => (
                            <div key={u.id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-rule hover:bg-surface items-center" data-testid={`admin-user-row-${u.id}`}>
                                <div className="col-span-3 min-w-0">
                                    <div className="font-semibold text-sm truncate">{u.prenom || ""} {u.nom || ""}</div>
                                    <div className="text-[11px] font-mono text-muted2 truncate">{u.email}</div>
                                </div>
                                <div className="col-span-2">
                                    <span className={`pill !text-[10px] ${ROLE_PILL[u.role] || ""}`}>{u.role}</span>
                                </div>
                                <div className="col-span-3 text-xs text-ink/70 truncate">
                                    {u.role === "responsable" && u.invite_code ? (
                                        <span><span className="font-mono text-orange">{u.invite_code}</span> · {u.nom_etablissement || "—"}</span>
                                    ) : (
                                        u.nom_etablissement || "—"
                                    )}
                                </div>
                                <div className="col-span-2 text-[11px] font-mono text-muted2">
                                    {u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "—"}
                                </div>
                                <div className="col-span-2 flex items-center justify-end gap-1.5">
                                    {u.role !== "admin" && (
                                        <button
                                            onClick={() => impersonate(u)}
                                            className="p-1.5 hover:bg-orange/10 text-ink/70 hover:text-orange"
                                            title="Se connecter en tant que"
                                            data-testid={`admin-user-impersonate-${u.id}`}
                                        >
                                            <LogIn size={14} />
                                        </button>
                                    )}
                                    {u.role !== "admin" && (
                                        <button
                                            onClick={() => resetPwd(u)}
                                            className="p-1.5 hover:bg-blue-50 text-ink/70 hover:text-blue-700"
                                            title="Reset MDP"
                                            data-testid={`admin-user-resetpwd-${u.id}`}
                                        >
                                            <KeyRound size={14} />
                                        </button>
                                    )}
                                    {u.role !== "admin" && (
                                        <button
                                            onClick={() => removeUser(u)}
                                            className="p-1.5 hover:bg-destructive/10 text-ink/70 hover:text-destructive"
                                            title="Supprimer"
                                            data-testid={`admin-user-delete-${u.id}`}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    )}
                                    {u.role === "admin" && (
                                        <ShieldCheck size={14} className="text-amber-700" title="Admin" />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
